import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PaginationProps {
    page: number
    pageCount: number
    hrefFor: (page: number) => string
}

// Page numbers with the first, last and neighbours of the current page, and gaps in between.
const getPages = (page: number, pageCount: number): Array<number | 'gap'> => {
    const pages = new Set([1, pageCount, page - 1, page, page + 1])
    const sorted = [...pages].filter(value => value >= 1 && value <= pageCount).sort((a, b) => a - b)
    const result: Array<number | 'gap'> = []

    for (const value of sorted) {
        const last = result[result.length - 1]

        if (typeof last === 'number' && value - last > 1) result.push('gap')

        result.push(value)
    }

    return result
}

const itemClasses = 'inline-flex h-10 min-w-10 items-center justify-center gap-1 rounded-xl px-3 text-sm font-medium transition focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'

export const Pagination: FC<PaginationProps> = ({ page, pageCount, hrefFor }): ReactNode => {
    if (pageCount <= 1) return null

    return (
        <nav aria-label='Pagination' className='flex flex-wrap items-center justify-center gap-1.5'>
            {page > 1 ? (
                <Link href={hrefFor(page - 1)} className={cn(itemClasses, 'border border-white/10 hover:bg-white/[0.06]')}>
                    <ChevronLeft aria-hidden='true' className='size-4' />
                    Previous
                </Link>
            ) : (
                <span aria-disabled='true' className={cn(itemClasses, 'border border-white/[0.06] text-muted-foreground/60')}>
                    <ChevronLeft aria-hidden='true' className='size-4' />
                    Previous
                </span>
            )}

            <ul className='flex items-center gap-1.5'>
                {getPages(page, pageCount).map((value, position) => (
                    <li key={value === 'gap' ? `gap-${position}` : value}>
                        {value === 'gap' ? (
                            <span aria-hidden='true' className='px-1 text-muted-foreground'>
                                …
                            </span>
                        ) : (
                            <Link
                                href={hrefFor(value)}
                                aria-current={value === page ? 'page' : undefined}
                                className={cn(itemClasses, value === page ? 'bg-primary text-primary-foreground' : 'hover:bg-white/[0.06]')}
                            >
                                <span className='sr-only'>Page</span>
                                {value}
                            </Link>
                        )}
                    </li>
                ))}
            </ul>

            {page < pageCount ? (
                <Link href={hrefFor(page + 1)} className={cn(itemClasses, 'border border-white/10 hover:bg-white/[0.06]')}>
                    Next
                    <ChevronRight aria-hidden='true' className='size-4' />
                </Link>
            ) : (
                <span aria-disabled='true' className={cn(itemClasses, 'border border-white/[0.06] text-muted-foreground/60')}>
                    Next
                    <ChevronRight aria-hidden='true' className='size-4' />
                </span>
            )}
        </nav>
    )
}
